import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEsriMapContext } from '../EsriMapContext';
import EsriUtils from '../utils/esriHelpers';
import type { HoverPopupPayload } from '../types';
import { MapFunction } from '@Map/types';
import { debounce, functionalityEnabled } from '@Map/utils';

interface UseHoverPopupOptions {
	enabled?: boolean;
	debounceMs?: number;
}

const DEFAULT_HOVER_DEBOUNCE_MS = 200;
const UPDATING_NO_HIT_GRACE_MS = 500;

export const useHoverPopup = (options: UseHoverPopupOptions = {}) => {
	const { view, displayMode, isDrawing } = useEsriMapContext();
	const { enabled = true, debounceMs = DEFAULT_HOVER_DEBOUNCE_MS } = options;
	const hoverEnabled =
		enabled && functionalityEnabled(MapFunction.hoverPopup, displayMode);

	const [payload, setPayload] = useState<HoverPopupPayload>({
		features: undefined,
	});

	const viewRef = useRef(view);
	viewRef.current = view;
	const hasPopupRef = useRef(false);
	hasPopupRef.current = Boolean(payload.features?.length);

	const requestIdRef = useRef(0);
	const lastPrimaryHoverKeyRef = useRef('');
	const lastHitAtRef = useRef(0);

	const clearPayload = useCallback(() => {
		lastHitAtRef.current = 0;
		lastPrimaryHoverKeyRef.current = '';
		setPayload(prev => {
			if (!prev.features) return prev;
			return { features: undefined };
		});
	}, []);

	const clearHover = useCallback(() => {
		requestIdRef.current += 1;
		clearPayload();
	}, [clearPayload]);

	const resolveHoverAtPoint = useCallback(
		async (x: number, y: number) => {
			if (isDrawing) {
				clearHover();
				return;
			}

			const v = viewRef.current;
			if (!v || !hoverEnabled) {
				clearHover();
				return;
			}
			if (!v.map?.layers.length) {
				// Layer refresh windows can temporarily leave map.layers empty.
				// Preserve current hover payload until we get another stable hit test.
				if (hasPopupRef.current) return;
				clearHover();
				return;
			}

			const requestId = ++requestIdRef.current;
			const response = await v.hitTest(
				{ x, y } as unknown as __esri.ViewClickEvent,
				{
					include: v.map.layers,
				},
			);

			if (requestId !== requestIdRef.current) return;

			const hits = EsriUtils.viewHitTestResultsToSelectedAssets(response);
			if (!hits.length) {
				// During style/layer refreshes Esri may briefly return no hit for the
				// same cursor position. Keep popup stable briefly while the view updates,
				// but do not block clear forever in apps where updating stays true.
				if (v.updating) {
					const msSinceLastHit = Date.now() - lastHitAtRef.current;
					if (
						hasPopupRef.current &&
						msSinceLastHit <= UPDATING_NO_HIT_GRACE_MS
					) {
						return;
					}
					clearPayload();
					return;
				}
				clearPayload();
				return;
			}

			lastHitAtRef.current = Date.now();
			const hoverFeatures = hits.map(hit =>
				EsriUtils.selectedAssetToHoverPopupFeature(hit),
			);
			const primaryFeature = hoverFeatures[0];
			const primaryHoverKey = primaryFeature
				? `${primaryFeature.layerId ?? ''}:${primaryFeature.id ?? primaryFeature.assetId ?? ''}`
				: '';

			// Keep popup content stable while secondary hit order churns.
			// This matches Mapbox behavior where the first sorted feature drives content.
			if (primaryHoverKey === lastPrimaryHoverKeyRef.current) return;
			lastPrimaryHoverKeyRef.current = primaryHoverKey;

			setPayload({
				point: { x, y },
				features: hoverFeatures,
			});
		},
		[clearHover, clearPayload, hoverEnabled, isDrawing],
	);

	const debouncedResolveHover = useMemo(() => {
		return debounce((x: number, y: number) => {
			void resolveHoverAtPoint(x, y);
		}, debounceMs);
	}, [debounceMs, resolveHoverAtPoint]);

	const cancelPopupUpdate = useCallback(() => {
		// Match Mapbox behavior: when pointer enters popup content, prevent any
		// in-flight hover resolution/clear from hiding the popup unexpectedly.
		requestIdRef.current += 1;
		debouncedResolveHover.clear();
	}, [debouncedResolveHover]);

	useEffect(() => {
		if (!view || !hoverEnabled) {
			clearHover();
			return;
		}

		const pointerMoveHandle = view.on('pointer-move', event => {
			debouncedResolveHover(event.x, event.y);
		});

		const handleViewMouseOut = (event: MouseEvent) => {
			const nextTarget = event.relatedTarget as Node | null;
			const mapContainer = view.container?.parentElement;
			// Popup is rendered as a sibling of the ArcGIS view container.
			// Moving from view -> popup must not clear hover.
			if (
				mapContainer &&
				nextTarget &&
				mapContainer.contains(nextTarget)
			) {
				return;
			}
			debouncedResolveHover.clear();
			clearHover();
		};
		view.container?.addEventListener('mouseout', handleViewMouseOut);

		return () => {
			pointerMoveHandle.remove();
			view.container?.removeEventListener('mouseout', handleViewMouseOut);
			debouncedResolveHover.clear();
		};
	}, [view, hoverEnabled, debouncedResolveHover, clearHover]);

	useEffect(() => {
		if (!isDrawing) return;
		debouncedResolveHover.clear();
		clearHover();
	}, [isDrawing, debouncedResolveHover, clearHover]);

	useEffect(() => {
		return () => {
			debouncedResolveHover.clear();
		};
	}, [debouncedResolveHover]);

	return {
		hoveredAsset: payload.features?.[0] ?? null,
		hoverPopupPayload: payload,
		clearHover,
		cancelPopupUpdate,
	};
};

export type HoverPopup = ReturnType<typeof useHoverPopup>;
